import { Alert } from '@hl/ui-components';
import { FC, ReactNode, useEffect, useMemo, useRef } from 'react';

import { LoadingMessage } from '../Message/LoadingMessage';
import { Message } from '../Message/Message';
import { MessageContainerWrapper } from '../Message/Message.styled';
import { MESSAGE_ROLE, MessageType } from '../Message/types/messages';
import { ErrorWrapper } from './TranscriptMessages.styled';
import { useSanitize } from './utils/useSanitize';

export type MessageCustomizationProps = {
  renderMessage?: (message: MessageType) => ReactNode;
  renderAdditionalActions?: (message: MessageType) => ReactNode;
  renderAdditionalMessageUI?: (message: MessageType) => ReactNode;
};

type TranscriptMessagesProps = MessageCustomizationProps & {
  messages: MessageType[];
  failed?: boolean;
  isLoading?: boolean;
  loadingMessage?: ReactNode;
  initialMessage?: MessageType;
  assistantName?: string;
  avatarUrl?: string;
};

export const TranscriptMessages: FC<TranscriptMessagesProps> = ({
  messages,
  failed = false,
  isLoading = false,
  loadingMessage,
  initialMessage = null,
  renderMessage,
  renderAdditionalActions,
  renderAdditionalMessageUI,
  assistantName,
  avatarUrl,
}) => {
  const { sanitize, containsHTMLTags, containsMarkdown, extractMarkdown } =
    useSanitize();
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const loadingMessageRef = useRef<HTMLDivElement>(null);
  const allMessages = useMemo(() => {
    // if intitial message is present and it is not loading for the first time, add it to the messages array
    return initialMessage && !(isLoading && messages.length === 0)
      ? [initialMessage, ...messages]
      : messages;
  }, [initialMessage, messages, isLoading]);

  useEffect(() => {
    if (isLoading && loadingMessageRef.current) {
      loadingMessageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    } else if (allMessages.length && lastMessageRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        lastMessageRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  }, [isLoading, allMessages.length]);

  const maybeRenderError = useMemo(
    () =>
      failed && (
        <ErrorWrapper>
          {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
          <Alert danger={true}>
            An error occurred. Reloading the page may fix the issue.
          </Alert>
        </ErrorWrapper>
      ),
    [failed],
  );

  const maybeRenderMessages = useMemo(() => {
    return (
      !failed && (
        <>
          {allMessages.length > 0 && (
            <>
              {allMessages.map((message, messageIndex) => {
                const actionStates =
                  message.localActionStates ?? message.actionStates;
                const isLastMessage = messageIndex === allMessages.length - 1;

                if (
                  !renderMessage &&
                  (containsMarkdown(message.message) ||
                    containsHTMLTags(message.message)) &&
                  message.role === MESSAGE_ROLE.ASSISTANT
                ) {
                  const messageParts = extractMarkdown(message.message);

                  return messageParts.map((part, partIndex) => {
                    const isHtml = containsHTMLTags(part);
                    const isLastPart = partIndex === messageParts.length - 1;
                    // Last part is not rendered, so last RENDERED part is second-to-last
                    const isLastRenderedPart = partIndex === messageParts.length - 2;

                    const renderDangerously = () => {
                      return (
                        <section
                          dangerouslySetInnerHTML={{
                            __html: sanitize(part),
                          }}
                        />
                      );
                    };

                    return (
                      part &&
                      !isLastPart && (
                        <Message
                          message={part}
                          renderedMessage={
                            isHtml ? renderDangerously() : undefined
                          }
                          renderedAdditionalActions={renderAdditionalActions?.(
                            message,
                          )}
                          renderedAdditionalUI={renderAdditionalMessageUI?.(
                            message,
                          )}
                          id={message.messageId}
                          status={actionStates.liked}
                          failed={message.failed}
                          key={partIndex}
                          type={message.role}
                          ref={
                            isLastMessage && isLastRenderedPart
                              ? lastMessageRef
                              : undefined
                          }
                          hideReactions={!isHtml || message.hideReactions}
                          assistantName={assistantName}
                          avatarUrl={avatarUrl}
                        />
                      )
                    );
                  });
                } else {
                  return (
                    message.role !== MESSAGE_ROLE.SYSTEM && (
                      <Message
                        message={message.message}
                        renderedMessage={renderMessage?.(message)}
                        renderedAdditionalActions={renderAdditionalActions?.(
                          message,
                        )}
                        renderedAdditionalUI={renderAdditionalMessageUI?.(
                          message,
                        )}
                        id={message.messageId}
                        status={actionStates.liked}
                        failed={message.failed}
                        key={messageIndex}
                        type={message.role}
                        ref={
                          isLastMessage
                            ? lastMessageRef
                            : undefined
                        }
                        hideReactions={message.hideReactions}
                        assistantName={assistantName}
                        avatarUrl={avatarUrl}
                      />
                    )
                  );
                }
              })}
            </>
          )}
          {isLoading && (
            <LoadingMessage
              loadingMessage={loadingMessage}
              ref={loadingMessageRef}
            />
          )}
        </>
      )
    );
  }, [
    failed,
    allMessages,
    isLoading,
    loadingMessage,
    renderMessage,
    containsMarkdown,
    containsHTMLTags,
    extractMarkdown,
    renderAdditionalActions,
    renderAdditionalMessageUI,
    sanitize,
    assistantName,
    avatarUrl,
  ]);

  return (
    <MessageContainerWrapper role="log">
      <div role="list">
        <div />
        {maybeRenderError}
        {maybeRenderMessages}
      </div>
    </MessageContainerWrapper>
  );
};
